/*
 Licensed Materials - Property of IBM
 5737-I23
 Copyright IBM Corp. 2019. All Rights Reserved.
 U.S. Government Users Restricted Rights:
 Use, duplication or disclosure restricted by GSA ADP Schedule
 Contract with IBM Corp.
 */
import React, { useCallback, useEffect, useState } from 'react';

import { TextInput } from 'carbon-components-react';

import { useDirtyInput } from '../../common/useDirtyInput';

function CarbonBaseInput(props) {
    // Note: since React 15.2.0 we can't forward unknown element attributes, so we
    // exclude the "options" and "schema" ones here.
    if (!props.id) {
        throw new Error(`no id for props ${JSON.stringify(props)}`);
    }
    const {
        id,
        value,
        defaultValue,
        readonly,
        disabled,
        autofocus,
        onBlur,
        onChange,
        onFocus,
        options,
        schema,
        formContext,
        registry,
        rawErrors,
        ...inputProps
    } = props;

    // If options.inputType is set use that as the input type
    if (options.inputType) {
        inputProps.type = options.inputType;
    } else if (!inputProps.type) {
    // If the schema is of type number or integer, set the input type to number
        if (schema.type === 'number') {
            inputProps.type = 'number';
            // Setting step to 'any' fixes a bug in Safari where decimals are not
            // allowed in number inputs
            inputProps.step = 'any';
        } else if (schema.type === 'integer') {
            inputProps.type = 'number';
            // Since this is integer, you always want to step up or down in multiples
            // of 1
            inputProps.step = '1';
        } else {
            inputProps.type = 'text';
        }
    }

    // If multipleOf is defined, use this as the step value. This mainly improves
    // the experience for keyboard users (who can use the up/down KB arrows).
    if (schema.multipleOf) {
        inputProps.step = schema.multipleOf;
    }

    const _onChange = useCallback(({ target: { value }}) => {
        onChange(value === '' ? options.emptyValue : value);
    }, [onChange, options.emptyValue]);

    const [inputDirty, __onChange, _onBlur] = useDirtyInput(_onChange, onBlur, value, defaultValue);

    const [invalid, setInvalid] = useState(false);
    useEffect(() => {
        if (formContext && formContext.ignoreDirty) {
            setInvalid(schema.invalid);
        } else {
            setInvalid(inputDirty ? schema.invalid : false);
        }
    }, [formContext, inputDirty, schema.invalid]);

    return (
        <TextInput
            id={id}
            readOnly={readonly}
            disabled={disabled}
            hideLabel={true}
            invalid={invalid}
            invalidText={schema.invalid ? schema.invalidText : ''}
            value={value == null ? '' : value}
            defaultValue={defaultValue}
            onBlur={_onBlur}
            onChange={__onChange}
            labelText={inputProps.label ? inputProps.label : ''}
            {...inputProps}
        />
    );
}

CarbonBaseInput.defaultProps = {
    required: false,
    disabled: false,
    readonly: false,
    autofocus: false,
    options : {}
};

export { CarbonBaseInput };
